import React, { useMemo } from 'react';

import { Box, Divider } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import DataDisplay, { DataDisplayVariant } from '@sharedCR/coreDesignSystem/DataDisplay';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { hasPropertyChanged } from 'utils/loanApplication';
import { getFieldPendingChangeAuthors } from 'utils/pendingChangesAuthors';

import { getFundingInformationCardsConfig } from '../../LoanClosingCDS.config';
import { getOverviewWrapperStyles } from '../../LoanClosingCDS.styles';
import { getWireToInfo } from '../../LoanClosingCDS.utils';
import IconGroupPanel from '../IconGroupPanel';

import WireConfirmationBadge from './components/WireConfirmationBadge';
import { getIconGroupsConfig } from './FundingInformationSection.config';
import { titleWrapperStyles } from './FundingInformationSection.styles';

const FundingInformationSection = ({
  fundingInfo,
  warehouseBankOptions,
  pendingChangesData,
  fieldAuthors = null,
  isEditAllowed = false,
  isMenuOpened = false,
  isUnsecured = false,
  onMenuClick,
}) => {
  const { cardsConfig, wireToInfo } = useMemo(
    () => ({
      cardsConfig: getFundingInformationCardsConfig({
        fundingInfo,
        warehouseBankOptions,
        isUnsecured,
      }),
      wireToInfo: getWireToInfo({ fundingInfo, warehouseBankOptions }),
    }),
    [fundingInfo, warehouseBankOptions, isUnsecured],
  );

  const iconGroups = useMemo(
    () =>
      getIconGroupsConfig({
        wireToInfo,
        fundingInfo,
        pendingChangesData,
        fieldAuthors,
        isUnsecured,
      }),
    [wireToInfo, fundingInfo, pendingChangesData, fieldAuthors, isUnsecured],
  );

  return (
    <PageBlock
      withAction
      isActionDisabled={!isEditAllowed}
      isMenuOpened={isMenuOpened}
      title={
        <Box sx={titleWrapperStyles}>
          Funding Information
          <WireConfirmationBadge />
        </Box>
      }
      onMenuClick={onMenuClick}
    >
      <Box sx={getOverviewWrapperStyles(cardsConfig.length)}>
        {cardsConfig.map(({ id, name, value, icon, iconColor, pendingChangesKey }) => (
          <DataDisplay
            icon={icon}
            iconColor={iconColor}
            isModified={hasPropertyChanged(_.get(pendingChangesData, pendingChangesKey))}
            key={id}
            pendingChangeAuthors={getFieldPendingChangeAuthors(fieldAuthors, pendingChangesKey)}
            title={name}
            value={value}
            variant={DataDisplayVariant.STYLE_B}
          />
        ))}
      </Box>

      <Divider />

      <IconGroupPanel groups={iconGroups} />
    </PageBlock>
  );
};

FundingInformationSection.propTypes = {
  fieldAuthors: PropTypes.object,
  fundingInfo: PropTypes.object,
  isEditAllowed: PropTypes.bool,
  isMenuOpened: PropTypes.bool,
  isUnsecured: PropTypes.bool,
  pendingChangesData: PropTypes.object,
  warehouseBankOptions: PropTypes.array,
  onMenuClick: PropTypes.func,
};

export default FundingInformationSection;
